/**
 * PasswordField: a password input with a Show / Hide button and an optional strength meter (cockatrice-ui
 * `src/ui/PasswordField.jsx`, ported to hyperscript; PLAN.md §0).
 * The meter always says its level in words ("Weak", "Strong"), never by colour alone.
 * Pass `showStrength` for the built-in estimate, or `strength: (value) => ({ score: 0-4, label })`.
 */
import './css/password-field.css';
import { createEffect, createSignal, createUniqueId, mergeProps, splitProps } from 'solid-js';
import { named, useFieldAttrs, wrapControl } from './Field.ts';
import type { Without } from './Field.ts';
import { TextInput } from './TextField.ts';
import type { TextFieldProps } from './TextField.ts';
import { component, each, el, when } from './solid.ts';
import type { Attributes, Child, On } from './solid.ts';
import { uiText } from './text.ts';

/** A strength result: score 0 (too short) to 4 (strong) and the word shown. */
export interface PasswordStrength {
  readonly score: 0 | 1 | 2 | 3 | 4;
  readonly label: string;
}

export interface PasswordFieldProps extends Attributes, Without<TextFieldProps, 'type' | 'end' | 'iconStart' | 'prefix' | 'suffix'> {
  /** Visible label. When given (or help/error), the control wraps itself in a Field. */
  readonly label?: Child;
  /** Help text under the control, linked with aria-describedby. */
  readonly help?: Child;
  /** Error message. Sets aria-invalid and shows the message with a warning icon. */
  readonly error?: Child;
  /** Marks the field required and shows "Required". */
  readonly required?: boolean | undefined;
  /** Shows "Optional" next to the label. */
  readonly optional?: boolean | undefined;
  /** Class for the wrapping Field. */
  readonly fieldClass?: string | undefined;
  /** Shows the built-in local strength meter. */
  readonly showStrength?: boolean | undefined;
  /** Your own strength estimate; shows the meter. */
  readonly strength?: ((value: string) => PasswordStrength) | undefined;
  /**
   * The meter's words as a polite live region, read as you type (default true). false keeps them quiet while typing
   * (still in the input's description) and says the strength once as the focus leaves the field, input and Show button
   * alike; the same words are not said again until they change.
   */
  readonly strengthLive?: boolean | undefined;
  /** Defaults to current-password, or new-password when a meter is shown. */
  readonly autocomplete?: string | undefined;
  /** Text on the toggle while hidden. Default "Show". */
  readonly showLabel?: string | undefined;
  /** Text on the toggle while shown. Default "Hide". */
  readonly hideLabel?: string | undefined;
}

const WORDS = ['ui.password-field.too-short', 'ui.password-field.weak', 'ui.password-field.fair', 'ui.password-field.good', 'ui.password-field.strong'] as const;

/** A score from 1 to 4, for a password of 8 or more (`estimateStrength` gives a shorter one 0 before it gets here). */
const toScore = (n: number): PasswordStrength['score'] => (n <= 1 ? 1 : n === 2 ? 2 : n === 3 ? 3 : 4);

/** A simple local estimate: length first, then variety. Nothing leaves the device. */
export function estimateStrength(value: string | undefined): PasswordStrength {
  const v = value ?? '';
  if (v.length < 8) return { score: 0, label: uiText()(WORDS[0]) };
  let score = 1;
  if (v.length >= 12) score++;
  if (v.length >= 16) score++;
  const kinds = [/[a-z]/, /[A-Z]/, /\d/, /[^A-Za-z0-9]/].filter((r) => r.test(v)).length;
  if (kinds >= 3) score++;
  if (/^(.)\1+$/.test(v) || /^(password|qwerty|12345678)/i.test(v)) score = 1;
  const final = toScore(Math.min(score, 4));
  return { score: final, label: uiText()(WORDS[final]) };
}

const FIELD_ONLY = ['label', 'help', 'optional', 'fieldClass'] as const;

export default function PasswordField(props: PasswordFieldProps): Child {
  const [, rest] = splitProps(props, FIELD_ONLY);
  return wrapControl(props, () => component(PasswordInput, rest));
}

function PasswordInput(props: PasswordFieldProps): Child {
  const p = mergeProps(
    {
      get showLabel() {
        return uiText()('ui.password-field.show');
      },
      get hideLabel() {
        return uiText()('ui.password-field.hide');
      },
    },
    props,
  );
  const [local, rest] = splitProps(p, ['value', 'defaultValue', 'onChange', 'showStrength', 'strength', 'strengthLive', 'autocomplete', 'showLabel', 'hideLabel', 'aria-describedby']);
  const f = useFieldAttrs(props);
  const meterId = createUniqueId();
  const [shown, setShown] = createSignal(false);
  const [val, setVal] = createSignal(local.value ?? local.defaultValue ?? '');
  createEffect(() => {
    if (local.value !== undefined) setVal(local.value);
  });

  const hasMeter = (): boolean => local.showStrength === true || local.strength !== undefined;
  const result = (): PasswordStrength => (typeof local.strength === 'function' ? local.strength(val()) : estimateStrength(val()));
  const words = (): string => `${uiText()('ui.password-field.strength')} ${result().label}`;

  // strengthLive false: quiet while typing, the strength said once as the focus leaves the field (status announced
  // once, without chatter). The region is cleared when the focus comes back, so it never stands holding old news.
  const quiet = (): boolean => hasMeter() && local.strengthLive === false;
  const [said, setSaid] = createSignal('');
  let lastSaid = '';
  const leave = (event: On<FocusEvent, HTMLDivElement>): void => {
    const to = event.relatedTarget;
    if (!quiet() || (to instanceof Node && event.currentTarget.contains(to))) return;
    const now = val() === '' ? '' : words();
    if (now === lastSaid) return;
    lastSaid = now;
    setSaid(now);
  };

  const toggle = el(
    'button',
    {
      type: 'button',
      class: 'ui-password-field__toggle',
      'aria-pressed': () => (shown() ? 'true' : 'false'),
      'aria-label': () => (shown() ? uiText()('ui.password-field.hide-password') : uiText()('ui.password-field.show-password')),
      disabled: () => f.disabled(),
      onClick: () => setShown(!shown()),
    },
    () => (shown() ? local.hideLabel : local.showLabel),
  );

  return el(
    'div',
    {
      class: 'ui-password-field',
      onFocusIn: () => {
        setSaid('');
      },
      onFocusOut: leave,
    },
    component(
      TextInput,
      mergeProps(named(rest), {
        get type() {
          return shown() ? 'text' : 'password';
        },
        get value() {
          return local.value;
        },
        get defaultValue() {
          return local.defaultValue;
        },
        get autocomplete() {
          return local.autocomplete ?? (hasMeter() ? 'new-password' : 'current-password');
        },
        spellcheck: false,
        autocapitalize: 'off',
        get 'aria-describedby'() {
          const own = local['aria-describedby'];
          return [typeof own === 'string' ? own : undefined, hasMeter() && val() !== '' && meterId].filter(Boolean).join(' ') || undefined;
        },
        onChange: (v: string, event: InputEvent) => {
          setVal(v);
          local.onChange?.(v, event);
        },
        end: toggle,
      }),
    ),
    when(
      () => hasMeter() && val() !== '',
      () =>
        el(
          'div',
          { class: 'ui-password-field__meter', 'data-score': () => result().score },
          el(
            'div',
            { class: 'ui-password-field__bars', 'aria-hidden': 'true' },
            each(
              () => [1, 2, 3, 4],
              (n) => el('span', { class: () => (result().score >= n ? 'ui-password-field__bar is-on' : 'ui-password-field__bar') }),
            ),
          ),
          el(
            'span',
            { class: 'ui-password-field__word', id: meterId, 'aria-live': () => (quiet() ? undefined : 'polite') },
            () => `${uiText()('ui.password-field.strength')} `,
            () => result().label,
          ),
        ),
    ),
    when(quiet, () => el('span', { class: 'sr-only ui-password-field__said', role: 'status' }, said)),
  );
}
